/**
 * Quota Revo della polizza: quanta parte del rischio e' a carico di REVO.
 *
 * OSSQ-2386. Prima di questa utility la formula era ripetuta in 7 componenti nella forma
 * `tipo === 'NOSTRA_DELEGA' ? ripartizionePerc / 100 : 1`, con due difetti:
 *
 * 1. ALTRUI_DELEGA cadeva nel ramo `: 1`, quindi la quota Revo era il 100% anche quando la
 *    polizza e' coassicurata con un'altra delegataria. I tre valori reali di
 *    `tipoCoassicurazione` sono ESCLUSIVA, NOSTRA_DELEGA e ALTRUI_DELEGA: solo la prima e'
 *    interamente a carico di Revo, le altre due hanno una ripartizione da applicare.
 * 2. La frazione (1 = 100%) veniva spedita come header `quota` a os-sinistri, che invece lavora
 *    in scala 0-100 e divide a sua volta per 100 (RiservaComplessivaFinancialMapper). Su polizza
 *    in esclusiva il campo "riserva indennizzo lorda quota revo" mostrava cosi' l'1% della lorda.
 *
 * Da qui le due funzioni: {@link quotaRevoPerc} e' il valore da mandare al backend (0-100),
 * {@link quotaRevoFrazione} quello per i calcoli fatti in locale dal frontend (0-1). Sono la
 * stessa quota in due scale: non vanno mischiate.
 *
 * `ripartizionePerc` arriva dal BFF gia' riferita a REVO: PolizzaUtils seleziona dall'array
 * `compagnieCoassicurazioni` la riga con codice compagnia ELB o REV. Non va quindi cercata la
 * compagnia qui.
 */

/** Unico tipo interamente a carico di Revo: non ha ripartizione da applicare. */
const ESCLUSIVA = 'ESCLUSIVA';

/** Quota usata quando il dato di polizza manca: conserva il comportamento storico (100%). */
const QUOTA_PIENA = 100;

const normalizzaTipo = (tipoCoassicurazione = '') => (
  (tipoCoassicurazione || '').toString().trim().toUpperCase().replace(/\s+/g, '_')
);

/**
 * Quota Revo in scala 0-100, da passare al backend (header/parametro `quota`).
 *
 * Esclusiva vale sempre 100, qualunque cosa dica `ripartizionePerc`: in esclusiva il rischio e'
 * tutto di Revo e la riserva in quota deve coincidere con la riserva lorda.
 *
 * Lo zero e' un valore legittimo (esiste in portafoglio) e viene restituito come tale: chi chiama
 * non deve trattarlo come dato mancante. Il fallback a 100 scatta solo quando la ripartizione
 * manca davvero o non e' un numero, per non ridurre una riserva per colpa di un dato assente.
 *
 * @param {{tipoCoassicurazione?: string, ripartizionePerc?: number|string}} [coassicurazione]
 *        nodo `datiPolizza.coassicurazione` del ticket
 * @returns {number} quota Revo fra 0 e 100
 */
export const quotaRevoPerc = (coassicurazione) => {
  if (normalizzaTipo(coassicurazione?.tipoCoassicurazione) === ESCLUSIVA) {
    return QUOTA_PIENA;
  }
  // null/undefined/'' vanno trattati come dato ASSENTE, non come zero: `Number(null)` vale 0 ed e'
  // finito, quindi senza questo controllo una ripartizione mancante azzererebbe la riserva in quota.
  const grezza = coassicurazione?.ripartizionePerc;
  if (grezza === null || grezza === undefined || grezza === '') {
    return QUOTA_PIENA;
  }
  const ripartizione = Number(grezza);
  return Number.isFinite(ripartizione) ? ripartizione : QUOTA_PIENA;
};

/**
 * La stessa quota di {@link quotaRevoPerc} in scala 0-1, per i calcoli che il frontend fa in
 * proprio (es. `riservaLorda * quotaRevoFrazione(...)`).
 *
 * @param {{tipoCoassicurazione?: string, ripartizionePerc?: number|string}} [coassicurazione]
 *        nodo `datiPolizza.coassicurazione` del ticket
 * @returns {number} quota Revo fra 0 e 1
 */
export const quotaRevoFrazione = (coassicurazione) => quotaRevoPerc(coassicurazione) / 100;
